import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchCategories, updateCategory, deleteCategory, createCategory, reorderCategories } from '../api';

const AdminCategoryManager = ({ user }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    
    // Create new category state
    const [newCategoryName, setNewCategoryName] = useState("");
    const [creating, setCreating] = useState(false);

    // Drag and drop state
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const loadCategories = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchCategories();
            // Filter out the 'all' category placeholder
            setCategories(data.filter(c => c.id !== 'all'));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) {
            alert("Please enter a category name");
            return;
        }
        setCreating(true);
        try {
            await createCategory(user.token, newCategoryName);
            setNewCategoryName("");
            loadCategories();
        } catch (err) {
            alert("Error creating category: " + err.message);
        } finally {
            setCreating(false);
        }
    };

    const handleEditClick = (cat) => {
        setEditingId(cat.id);
        setEditName(cat.label);
    };

    const handleSaveEdit = async () => {
        if (!editName.trim()) {
            alert("Category name cannot be empty");
            return;
        }
        try {
            await updateCategory(user.token, editingId, editName);
            setEditingId(null);
            loadCategories();
        } catch (err) {
            alert("Error updating category: " + err.message);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditName("");
    };

    const handleDeleteClick = (cat) => {
        setDeletingId(cat.id);
    };

    const confirmDelete = async () => {
        try {
            await deleteCategory(user.token, deletingId);
            setDeletingId(null);
            loadCategories();
        } catch (err) {
            alert("Error deleting category: " + err.message);
        }
    };

    const cancelDelete = () => {
        setDeletingId(null);
    };

    // Drag and Drop handlers
    const dragStart = (e, position) => {
        dragItem.current = position;
    };

    const dragEnter = (e, position) => {
        dragOverItem.current = position;
    };

    const drop = async () => {
        if (dragItem.current === null || dragOverItem.current === null) return;
        
        const copyListItems = [...categories];
        const dragItemContent = copyListItems[dragItem.current];
        copyListItems.splice(dragItem.current, 1);
        copyListItems.splice(dragOverItem.current, 0, dragItemContent);
        
        dragItem.current = null;
        dragOverItem.current = null;
        
        setCategories(copyListItems);

        // Send to backend
        try {
            const orderedIds = copyListItems.map(c => c.id);
            await reorderCategories(user.token, orderedIds);
        } catch (err) {
            alert("Error reordering categories: " + err.message);
            loadCategories(); // reload to previous order
        }
    };

    if (loading && categories.length === 0) return <div style={{ padding: "20px", textAlign: "center" }}>Loading categories...</div>;
    if (error) return <div style={{ padding: "20px", color: "red", textAlign: "center" }}>{error}</div>;

    return (
        <div style={{ background: "white", padding: "24px", borderRadius: "12px", border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h2 style={{ margin: 0, color: "var(--forest)" }}>Manage Categories</h2>
            </div>
            
            <form onSubmit={handleCreateCategory} style={{ display: "flex", gap: "12px", marginBottom: "24px", padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid var(--border)" }}>
                <input 
                    type="text" 
                    placeholder="New category name..."
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    style={{ flex: 1, padding: "10px 14px", borderRadius: "8px", border: "1px solid var(--border)", fontFamily: "inherit" }}
                />
                <button type="submit" disabled={creating} className="btn btn-forest" style={{ padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer" }}>
                    {creating ? "Creating..." : "+ Add Category"}
                </button>
            </form>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {categories.length === 0 ? (
                    <p style={{ color: "var(--text-light)" }}>No categories found.</p>
                ) : (
                    categories.map((cat, index) => (
                        <div 
                            key={cat.id} 
                            draggable
                            onDragStart={(e) => dragStart(e, index)}
                            onDragEnter={(e) => dragEnter(e, index)}
                            onDragEnd={drop}
                            onDragOver={(e) => e.preventDefault()}
                            style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "space-between", 
                                padding: "16px", 
                                border: "1px solid var(--border)", 
                                borderRadius: "8px",
                                background: "#fafafa",
                                cursor: "grab"
                            }}
                        >
                            {editingId === cat.id ? (
                                <div style={{ display: "flex", gap: "10px", flex: 1, alignItems: "center" }}>
                                    <input 
                                        type="text" 
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        style={{ flex: 1, padding: "8px", borderRadius: "4px", border: "1px solid var(--border)" }}
                                    />
                                    <button onClick={handleSaveEdit} className="btn btn-primary" style={{ padding: "8px 16px" }}>Save</button>
                                    <button onClick={handleCancelEdit} className="btn btn-secondary" style={{ padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px" }}>Cancel</button>
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                        <div style={{ color: "#aaa", cursor: "grab", marginRight: "8px" }}>
                                            &#x2630; {/* Hamburger icon to signify drag */}
                                        </div>
                                        <span style={{ fontSize: "1.1rem", fontWeight: "600" }}>{cat.label}</span>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-light)", background: "#eee", padding: "2px 8px", borderRadius: "10px" }}>
                                            {cat.count} book(s)
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <button onClick={() => handleEditClick(cat)} className="btn btn-primary" style={{ padding: "6px 12px", background: "#C8960A", border: "none", borderRadius: "6px" }}>Edit</button>
                                        <button onClick={() => handleDeleteClick(cat)} className="btn btn-primary" style={{ padding: "6px 12px", background: "#dc3545", border: "none", borderRadius: "6px" }}>Delete</button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>

            {deletingId && (() => {
                const catToDelete = categories.find(c => c.id === deletingId);
                return (
                    <div className="delete-modal-overlay" onClick={cancelDelete}>
                        <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="delete-modal-icon">🗑️</div>
                            <h3>Delete Category?</h3>
                            <p>Are you sure you want to delete this category?</p>
                            <p>
                                <span className="book-title-highlight">{catToDelete ? catToDelete.label : ''}</span>
                            </p>
                            <p style={{ fontSize: '0.8rem', color: '#dc3545', fontWeight: 600, marginTop: '8px' }}>
                                ⚠️ This action will hide this category from the frontend.
                            </p>
                            <div className="delete-modal-actions">
                                <button className="delete-modal-cancel" onClick={cancelDelete}>Cancel</button>
                                <button className="delete-modal-confirm" onClick={confirmDelete}>Yes, Delete</button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default AdminCategoryManager;
